import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sparkles, PartyPopper, HandshakeIcon, CalendarClock, ArrowRight, ArrowLeft,
  CheckCircle2, X, Ticket, Users, Search, Rocket, Clock, Phone,
} from "lucide-react";

/**
 * ProspectIntakeModal — shown once, right after a client signs up,
 * BEFORE the walkthrough tour. 4-step onboarding:
 *   1) Are you working with us or a partner?
 *   2) If yes: enter their name / referral code (live validated).
 *   3) Pick a kickoff-call time so we can hear their goals.
 *   4) Success + "this is just a preview — the real intake happens on our call".
 */
export default function ProspectIntakeModal({ onDone }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [signupReferral, setSignupReferral] = useState(null); // {code, name} if pre-attached at signup
  const [answers, setAnswers] = useState({
    has_partner: "",       // 'yes' | 'no' | 'unsure'
    partner_code: "",
    partner_info: null,    // {valid, label, owner_name}
    scheduled_at: "",
    date: "",              // YYYY-MM-DD
    slots: [],
    loadingSlots: false,
    notes: "",
    phone: user?.phone || "",   // pre-fill from signup; required to book the call
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  });

  // Keep phone in sync once the auth user finishes loading
  useEffect(() => {
    if (user?.phone && !answers.phone) {
      setAnswers((p) => ({ ...p, phone: user.phone }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone]);

  // 1) On mount, check if this user has already completed prospect intake.
  useEffect(() => {
    // Pause the walkthrough immediately so it can never race ahead of us.
    // We'll fire resume() below if the user has already completed intake.
    try { window.dispatchEvent(new CustomEvent("cq:walkthrough:pause")); } catch {}
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/client/prospect-intake");
        if (!alive) return;
        if (!data.done) {
          setVisible(true);
          // If they already entered a referral code at signup, skip the
          // "who sent you?" question and jump straight to booking the call.
          if (data.signup_referral_code) {
            setSignupReferral({ code: data.signup_referral_code, name: data.signup_referral_name });
            setAnswers((p) => ({
              ...p,
              has_partner: "yes",
              partner_code: data.signup_referral_code,
              partner_info: { valid: true, code: data.signup_referral_code, owner_name: data.signup_referral_name, label: data.signup_referral_name },
            }));
            setStep(2);
          }
        } else {
          onDone && onDone(data);
          try { window.dispatchEvent(new CustomEvent("cq:walkthrough:resume")); } catch {}
        }
      } catch {
        // If endpoint fails (not a client), let the walkthrough resume.
        try { window.dispatchEvent(new CustomEvent("cq:walkthrough:resume")); } catch {}
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  // Live validate the code
  useEffect(() => {
    const code = (answers.partner_code || "").trim().toUpperCase();
    if (!code) { setAnswers((p) => ({ ...p, partner_info: null })); return; }
    const t = setTimeout(() => {
      api.post("/referrals/validate", { code })
        .then(({ data }) => setAnswers((p) => ({ ...p, partner_info: data?.valid ? data : { valid: false } })))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [answers.partner_code]);

  const loadSlots = async (date) => {
    setAnswers((p) => ({ ...p, date, slots: [], loadingSlots: true, scheduled_at: "" }));
    try {
      const { data } = await api.get(`/meetings/slots?date=${date}`);
      setAnswers((p) => ({ ...p, slots: data.slots || [], loadingSlots: false }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load slots");
      setAnswers((p) => ({ ...p, loadingSlots: false }));
    }
  };

  const submit = async () => {
    // Guard: phone is required to actually make the call.
    const cleanPhone = (answers.phone || "").replace(/[^\d+]/g, "");
    if (answers.scheduled_at && cleanPhone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a phone number we can reach you at for the call.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/client/prospect-intake", {
        has_partner: answers.has_partner,
        partner_code: (answers.partner_code || "").toUpperCase(),
        scheduled_at: answers.scheduled_at || null,
        timezone: answers.timezone,
        notes: answers.notes,
        phone: answers.phone,
      });
      setStep(3);
      onDone && onDone({ done: true, ...data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.post("/client/prospect-intake/skip");
      setVisible(false);
      // Still push them to set a goal so the whole portal personalizes.
      try { window.dispatchEvent(new CustomEvent("cq:focus-goal")); } catch {}
      onDone && onDone({ done: true, skipped: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const finish = () => {
    setVisible(false);
    // Scroll to the Goal Cockpit and flash a highlight so it's the first
    // thing they interact with. The walkthrough will auto-launch AFTER
    // they set their goal (see WalkthroughTour listener for cq:goal:saved).
    try { window.dispatchEvent(new CustomEvent("cq:focus-goal")); } catch {}
  };

  if (!visible) return null;

  // Next 14 date options
  const nextDates = Array.from({ length: 10 }).map((_, i) => {
    const d = new Date(); d.setDate(d.getDate() + 1 + i);
    return {
      value: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    };
  });

  return (
    <div className="fixed inset-0 z-[110] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 cq-in" data-testid="prospect-intake-modal">
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header ribbon */}
        <div className="relative h-24 bg-gradient-to-br from-amber-400 via-amber-500 to-rose-400 overflow-hidden">
          <div className="cq-grain absolute inset-0 opacity-60" />
          <div className="relative h-full flex items-end justify-between p-5">
            <div className="text-white">
              <p className="text-[10px] uppercase tracking-[0.28em] font-black opacity-90">Welcome to Credit Quack</p>
              <p className="font-display font-black text-2xl sm:text-3xl leading-none">Let's get you set up</p>
            </div>
            <button
              onClick={skip}
              disabled={!!signupReferral}
              className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest ${signupReferral ? "text-white/40 cursor-not-allowed" : "text-white/90 hover:text-white"}`}
              data-testid="prospect-skip"
              title={signupReferral ? "Your referral is locked in — please book your kickoff call." : ""}
            >
              {signupReferral ? "Please book your call" : (<>Skip for now <X size={14} /></>)}
            </button>
          </div>
        </div>

        {/* Step indicator */}
        <div className="px-5 sm:px-8 pt-4 flex items-center gap-2">
          {[0, 1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 rounded-full flex-1 transition ${s <= step ? "bg-amber-500" : "bg-slate-200"}`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-5 sm:px-8 py-6 min-h-[320px]">
          {step === 0 && (
            <div className="space-y-4" data-testid="prospect-step-0">
              <p className="text-xs uppercase tracking-widest font-black text-amber-600 flex items-center gap-1"><HandshakeIcon size={14} /> A quick question</p>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900">Are you already working with us or a partner?</h2>
              <p className="text-slate-600 text-sm">If someone gave you a code or referral link, drop it in on the next step so we can loop them in and personalize your kickoff call.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                {[
                  { v: "yes", label: "Yes — I have a code or a person", icon: <Ticket size={16} />, tone: "amber" },
                  { v: "unsure", label: "Someone told me about you", icon: <Users size={16} />, tone: "sky" },
                  { v: "no", label: "Nope, found you on my own", icon: <Search size={16} />, tone: "slate" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setAnswers((p) => ({ ...p, has_partner: opt.v }))}
                    data-testid={`prospect-answer-${opt.v}`}
                    className={`text-left p-4 rounded-2xl border-2 transition ${
                      answers.has_partner === opt.v
                        ? "border-amber-500 bg-amber-50 shadow-md"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className={`inline-flex h-8 w-8 rounded-lg items-center justify-center mb-2 ${
                      opt.tone === "amber" ? "bg-amber-100 text-amber-700" :
                      opt.tone === "sky" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"
                    }`}>{opt.icon}</div>
                    <p className="font-bold text-sm text-slate-900 leading-snug">{opt.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="prospect-step-1">
              <p className="text-xs uppercase tracking-widest font-black text-amber-600 flex items-center gap-1"><Ticket size={14} /> Referral code</p>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900">Who sent you our way?</h2>
              <p className="text-slate-600 text-sm">Enter their code or name so we can attach your account to them. If you're not sure, just skip.</p>
              <div>
                <Label>Code</Label>
                <Input
                  value={answers.partner_code}
                  onChange={(e) => setAnswers((p) => ({ ...p, partner_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. WELCOME2026"
                  className="h-12 text-lg font-bold tracking-widest"
                  data-testid="prospect-partner-code"
                />
                {answers.partner_info?.valid && (
                  <p className="mt-2 text-sm text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="prospect-code-valid">
                    <CheckCircle2 size={14} /> You'll be attributed to {answers.partner_info.owner_name || answers.partner_info.label}
                  </p>
                )}
                {answers.partner_code && answers.partner_info && !answers.partner_info.valid && (
                  <p className="mt-2 text-sm text-rose-600 font-bold" data-testid="prospect-code-invalid">
                    We don't recognize that code — double-check with your partner or skip.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="prospect-step-2">
              <p className="text-xs uppercase tracking-widest font-black text-amber-600 flex items-center gap-1"><CalendarClock size={14} /> Kickoff call</p>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900">Let's hear your goals</h2>
              {signupReferral && (
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-sm text-emerald-800 font-bold" data-testid="prospect-referral-locked">
                  <CheckCircle2 size={14} /> Referred by {signupReferral.name || signupReferral.code} — locked in
                </div>
              )}
              <p className="text-slate-600 text-sm">Pick a time for a quick 30-minute call. We'll listen to what you're building toward, then start your real intake together — no rushing, no guessing.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <select
                    value={answers.date}
                    onChange={(e) => loadSlots(e.target.value)}
                    className="mt-1 w-full h-11 rounded-md border border-slate-200 px-3 text-sm"
                    data-testid="prospect-date"
                  >
                    <option value="">Choose a day…</option>
                    {nextDates.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Best number to call</Label>
                  <div className="mt-1 relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      type="tel"
                      inputMode="tel"
                      value={answers.phone}
                      onChange={(e) => setAnswers((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="+1 555 0100"
                      className="pl-9 h-11"
                      data-testid="prospect-phone"
                      required
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">We'll call this number at your booked time. Timezone: <b>{answers.timezone || "UTC"}</b>.</p>
                </div>
              </div>

              {answers.date && (
                <div>
                  <Label>Available times</Label>
                  {answers.loadingSlots && <p className="text-sm text-slate-400 mt-2">Loading…</p>}
                  {!answers.loadingSlots && answers.slots.length === 0 && (
                    <p className="text-sm text-slate-400 mt-2">No slots for this day — pick another.</p>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {answers.slots.filter((s) => s.available).map((s) => (
                      <button
                        key={s.iso}
                        onClick={() => setAnswers((p) => ({ ...p, scheduled_at: s.iso }))}
                        data-testid={`prospect-slot-${s.iso}`}
                        className={`h-10 rounded-lg border text-sm font-bold transition ${
                          answers.scheduled_at === s.iso
                            ? "bg-amber-500 border-amber-500 text-white"
                            : "border-slate-200 hover:border-amber-400 text-slate-700"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label>Anything you'd love us to know? (optional)</Label>
                <Textarea
                  rows={2}
                  value={answers.notes}
                  onChange={(e) => setAnswers((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Buying a home in 12 months, need to raise scores 80 pts…"
                  data-testid="prospect-notes"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-6 space-y-3" data-testid="prospect-step-3">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <PartyPopper size={30} />
              </div>
              <h2 className="font-display font-black text-3xl text-slate-900">You're all set!</h2>
              <p className="text-slate-600 max-w-md mx-auto">
                {answers.scheduled_at
                  ? <>We've booked your kickoff call for <b>{new Date(answers.scheduled_at).toLocaleString()}</b>. We'll text and email you a reminder.</>
                  : <>We got you. Someone from our team will reach out to lock in a time.</>
                }
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 max-w-md mx-auto text-left">
                <p className="text-xs uppercase tracking-widest font-black text-amber-700 flex items-center gap-1"><Rocket size={12} /> What happens next</p>
                <p className="text-sm text-slate-700 mt-1">
                  Right after you tap below, we'll ask you <b>one big question</b>: what are you actually chasing? Once you tell us your goal, the whole portal <b>rebrands itself</b> around it, and your walkthrough becomes about <b>how we get you there</b> — not generic feature tours.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-8 pb-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="prospect-back"
            className={step === 0 || step === 3 ? "invisible" : ""}
          >
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>

          {step === 0 && (
            <Button
              onClick={() => setStep(answers.has_partner === "no" ? 2 : 1)}
              disabled={!answers.has_partner}
              className="bg-amber-500 hover:bg-amber-600 text-white h-11 px-6 rounded-full"
              data-testid="prospect-next"
            >
              Next <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={() => setStep(2)}
              className="bg-amber-500 hover:bg-amber-600 text-white h-11 px-6 rounded-full"
              data-testid="prospect-next"
            >
              Next <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-6 rounded-full"
              data-testid="prospect-submit"
            >
              {busy ? "Saving…" : <>Confirm <Sparkles size={14} className="ml-1" /></>}
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={finish}
              className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6 rounded-full ml-auto"
              data-testid="prospect-finish"
            >
              Set my goal <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
